import { useState } from 'react';
import { Share2 } from 'lucide-react';
import { GoldBadge } from '@/components/Luxury';
import CruxLogo from '@/assets/crux-logo.svg';
import { ShareModal } from './ShareModal';

/** Market status derived from deal_type and asking_price presence */
export type MarketStatus = 'on_market' | 'off_market';

interface PropertyHeroProps {
  address: string;
  location: string;
  tier: number;
  units: number;
  bedrooms?: string;
  /** Phase 212: Unit mix summary badge (e.g. "2u: 2bd/1ba each") */
  unitMix?: string;
  latitude?: number;
  longitude?: number;
  suggestedOffer?: number;
  /** Asking price from listing (on-market properties) */
  askingPrice?: number;
  verdict?: 'GO' | 'NOGO';
  dealType?: 'off-market' | 'on-market' | string;
  /** Property ID for PDF generation */
  propertyId?: string;
}

/**
 * Luxury hero header with Google Maps photos, CRUX RE branding, and property title.
 *
 * Features:
 * - Split hero: Street view (left) + Aerial view (right) - 300px height
 * - CRUX RE logo with Russo One brand name
 * - Large DM Serif Display property title (56px)
 * - Property tags (GoldBadge: "TIER X", "OFF-MARKET")
 * - GO/NOGO verdict badge (positioned top-right, large)
 * - Suggested offer (if provided, right side)
 *
 * Uses Google Maps Static API for images with fallback gray placeholder
 * if coordinates not available.
 */
export function PropertyHero({
  address,
  location,
  tier,
  units,
  bedrooms,
  unitMix,
  latitude,
  longitude,
  suggestedOffer,
  askingPrice,
  verdict,
  dealType,
  propertyId,
}: PropertyHeroProps) {
  const [showShareModal, setShowShareModal] = useState(false);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  const hasCoordinates = latitude !== undefined && longitude !== undefined;

  // Derive market status: prioritize explicit dealType, fallback to asking price presence
  // IMPORTANT: dealType takes precedence - an off-market deal may have an asking price
  // from seller negotiations but that doesn't make it a public listing
  const isOnMarket = dealType
    ? (dealType === 'on-market' || dealType === 'on_market')
    : Boolean(askingPrice && askingPrice > 0);
  const marketStatus: MarketStatus = isOnMarket ? 'on_market' : 'off_market';

  // Generate Google Maps Static API URLs
  const getStreetViewUrl = () => {
    if (!hasCoordinates || !apiKey) return null;
    return (
      `https://maps.googleapis.com/maps/api/streetview?` +
      `size=600x300&` +
      `location=${latitude},${longitude}&` +
      `fov=90&pitch=0&` +
      `key=${apiKey}`
    );
  };

  const getAerialViewUrl = () => {
    if (!hasCoordinates || !apiKey) return null;
    return (
      `https://maps.googleapis.com/maps/api/staticmap?` +
      `center=${latitude},${longitude}&` +
      `zoom=18&` +
      `size=600x300&` +
      `maptype=satellite&` +
      `markers=color:gold%7C${latitude},${longitude}&` +
      `key=${apiKey}`
    );
  };

  const streetViewUrl = getStreetViewUrl();
  const aerialViewUrl = getAerialViewUrl();

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="bg-obsidian relative">
      {/* Hero Images - Split View */}
      <div className="flex w-full gap-1">
        {/* Street View (Left) */}
        <div className="flex-1 relative h-[300px]">
          {streetViewUrl ? (
            <img
              src={streetViewUrl}
              alt={`Street view of ${address}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <div className={`w-full h-full bg-graphite flex items-center justify-center ${streetViewUrl ? 'hidden' : ''}`}>
            <span className="text-stone-700 text-sm uppercase tracking-wider">Street View</span>
          </div>
          <div className="absolute bottom-3 left-3 text-sm font-bold tracking-[0.1em] text-white bg-black/70 px-2.5 py-1.5 rounded">
            STREET VIEW
          </div>
        </div>

        {/* Aerial View (Right) */}
        <div className="flex-1 relative h-[300px]">
          {aerialViewUrl ? (
            <img
              src={aerialViewUrl}
              alt={`Aerial view of ${address}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <div className={`w-full h-full bg-graphite flex items-center justify-center ${aerialViewUrl ? 'hidden' : ''}`}>
            <span className="text-stone-700 text-sm uppercase tracking-wider">Aerial View</span>
          </div>
          <div className="absolute bottom-3 left-3 text-sm font-bold tracking-[0.1em] text-white bg-black/70 px-2.5 py-1.5 rounded">
            AERIAL VIEW
          </div>
        </div>
      </div>

      {/* Top Right Actions - Verdict Badge + Share Button */}
      <div className="absolute top-12 right-16 z-10 flex items-center gap-4">
        {/* Share Button (prominent for on-market) */}
        {marketStatus === 'on_market' && (
          <button
            onClick={() => setShowShareModal(true)}
            className="flex items-center gap-2 px-4 py-3 bg-amber-500/15 border border-amber-500 text-amber-400 hover:bg-amber-500/25 transition-colors rounded-lg"
          >
            <Share2 size={18} />
            <span className="font-brand text-sm tracking-[0.1em]">SHARE</span>
          </button>
        )}

        {/* Verdict Badge */}
        {verdict && (
          <div
            className={`
              px-8 py-4 font-brand text-lg tracking-[0.15em] border-2
              ${verdict === 'GO'
                ? 'bg-emerald/15 border-emerald text-emerald'
                : 'bg-crimson/15 border-crimson text-crimson'
              }
            `}
          >
            {verdict}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="px-12 py-8">
        {/* CRUX RE Logo */}
        <div className="flex items-center gap-4 mb-12">
          <img src={CruxLogo} alt="CRUX RE" className="w-12 h-12" />
          <div className="flex flex-col">
            <span className="font-brand text-2xl tracking-[0.1em] text-ivory">
              CRUX RE
            </span>
            <span className="text-sm tracking-[0.2em] text-gold uppercase font-medium">
              ACQUISITIONS INTELLIGENCE
            </span>
          </div>
        </div>

        {/* Property Title */}
        <div className="mb-8">
          <h1 className="font-serif text-[56px] font-normal text-ivory tracking-[-0.02em] leading-[1.1]">
            {address}
          </h1>
          <div className="text-lg text-stone-700 mt-2 font-light">
            {location}
          </div>
        </div>

        {/* Property Tags */}
        <div className="flex gap-3 flex-wrap mb-10">
          <GoldBadge variant="gold">{units} Unit{units !== 1 ? 's' : ''}</GoldBadge>
          {/* Phase 212: Unit mix summary badge */}
          {unitMix && (
            <GoldBadge variant="muted">{unitMix}</GoldBadge>
          )}
          {bedrooms && !unitMix && (
            <span className="inline-flex items-center justify-center rounded-full px-4 py-1.5 text-sm font-semibold uppercase tracking-[0.1em] border border-white/20 text-stone-700">
              {bedrooms}
            </span>
          )}
          <span className="inline-flex items-center justify-center rounded-full px-4 py-1.5 text-sm font-semibold uppercase tracking-[0.1em] border border-white/20 text-stone-700">
            Tier {tier} Submarket
          </span>
          {marketStatus === 'on_market' ? (
            <span className="inline-flex items-center justify-center rounded-full px-4 py-1.5 text-sm font-semibold uppercase tracking-[0.1em] bg-amber-500/15 border border-amber-500 text-amber-400">
              LISTED
            </span>
          ) : (
            <GoldBadge variant="off-market">OFF-MARKET</GoldBadge>
          )}
        </div>

        {/* Price Display: Asking Price (on-market) or Suggested Offer (off-market) */}
        {marketStatus === 'on_market' && askingPrice && askingPrice > 0 ? (
          <div className="flex items-end justify-between">
            <div>
              <div className="text-sm uppercase tracking-[0.15em] text-amber-400/70 font-semibold mb-1">
                Asking Price
              </div>
              <div className="text-4xl font-mono tabular-nums font-bold text-amber-400">
                {formatCurrency(askingPrice)}
              </div>
            </div>
            {/* Show suggested offer as secondary if available */}
            {suggestedOffer !== undefined && suggestedOffer > 0 && (
              <div className="text-right">
                <div className="text-sm uppercase tracking-[0.15em] text-stone-700 font-semibold mb-1">
                  Your Target
                </div>
                <div className="text-2xl font-mono tabular-nums font-medium text-gold">
                  {formatCurrency(suggestedOffer)}
                </div>
                <div className="text-sm text-emerald mt-1">
                  {((1 - suggestedOffer / askingPrice) * 100).toFixed(0)}% below ask
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Share Modal */}
      <ShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        propertyAddress={address}
        askingPrice={askingPrice}
        suggestedOffer={suggestedOffer}
        units={units}
        neighborhood={location}
        propertyId={propertyId}
      />
    </div>
  );
}
